import React, { useMemo } from 'react';
import { Box, Icon, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SCHEDULE_TAB_TYPE, getScheduleColour } from '../../../constants';

const WEEK_DAY_VISIBLE_LIMIT = 5;
const WEEK_STARTS_ON = 1;

const useStyles = makeStyles(() => ({
  weekView: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  weekRangeLabel: {
    flexShrink: 0,
    fontSize: '1.2rem',
    fontWeight: 600,
    color: '#117CA8',
    marginBottom: 8,
  },
  weekColumns: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    borderTop: '1px solid #E0E0E0',
  },
  dayColumn: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #E0E0E0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  dayColumnHeader: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 2px 10px',
    borderBottom: '1px solid #E0E0E0',
  },
  dayWeekday: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#70757a',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    lineHeight: 1.2,
  },
  dayWeekdayToday: {
    color: '#0B9FD9',
  },
  dayDateNumber: {
    marginTop: 4,
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.35rem',
    fontWeight: 500,
    color: '#3c4043',
  },
  dayDateNumberToday: {
    backgroundColor: '#0B9FD9',
    color: '#FFFFFF',
    fontWeight: 600,
  },
  dayColumnBody: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '4px 3px',
    gap: 3,
  },
  weekEventsList: {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    overflow: 'hidden',
  },
  weekEventCard: {
    flex: `0 0 calc((100% - ${(WEEK_DAY_VISIBLE_LIMIT - 1) * 3}px) / ${WEEK_DAY_VISIBLE_LIMIT})`,
    minHeight: 0,
    maxHeight: 56,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    boxSizing: 'border-box',
    padding: '3px 5px',
    border: '1px solid #DDE9EF',
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    textAlign: 'left',
    overflow: 'hidden',
    '&:hover': {
      boxShadow: '0px 2px 6px rgba(11, 159, 217, 0.14)',
    },
  },
  weekEventCardDone: {
    opacity: 0.72,
  },
  weekEventTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    minWidth: 0,
  },
  weekEventTypeIcon: {
    flexShrink: 0,
    fontSize: '1.35rem !important',
  },
  weekEventTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '1.05rem',
    fontWeight: 650,
    lineHeight: 1.25,
  },
  weekEventBottomRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    marginTop: 1,
    minWidth: 0,
  },
  weekEventTypeLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#6F8793',
    lineHeight: 1.2,
  },
  weekEventDoneIcon: {
    flexShrink: 0,
    fontSize: '1.25rem !important',
    color: '#ACDA0E',
  },
  weekMoreItem: {
    flex: `0 0 calc((100% - ${(WEEK_DAY_VISIBLE_LIMIT - 1) * 3}px) / ${WEEK_DAY_VISIBLE_LIMIT})`,
    minHeight: 0,
    maxHeight: 56,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    padding: '3px 8px',
    border: '1px solid #DDE9EF',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    textAlign: 'left',
    overflow: 'hidden',
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#3c4043',
    lineHeight: 1.25,
    '&:hover': {
      backgroundColor: '#F3FAFD',
      boxShadow: '0px 2px 6px rgba(11, 159, 217, 0.14)',
      color: '#0B9FD9',
    },
  },
}));

const WeekView = (props) => {
  const { data, onItemClick, onOpenDayDialog } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const weekDays = useMemo(() => {
    const validDates = (data || [])
      .map((item) => (item?.scheduleDate ? new Date(item.scheduleDate) : null))
      .filter((date) => date && isValid(date));
    const anchor = validDates.length > 0 ? validDates[0] : new Date();
    const start = startOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON });
    const end = endOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON });

    return eachDayOfInterval({ start, end }).map((day) => {
      const tasks = (data || []).filter((item) => {
        if (!item?.scheduleDate) return false;
        const itemDate = new Date(item.scheduleDate);
        return isValid(itemDate) && isSameDay(itemDate, day);
      });
      return { date: day, tasks };
    });
  }, [data]);

  const renderWeekEventCard = (value) => {
    const typeInfo = SCHEDULE_TAB_TYPE[value.type] || {};
    const itemColour = getScheduleColour(value);
    return (
      <button
        key={value?.id}
        type="button"
        className={clsx(classes.weekEventCard, value?.isDone && classes.weekEventCardDone)}
        style={{ borderLeftColor: itemColour }}
        title={`${value?.title || ''}${typeInfo.label ? ` (${typeInfo.label})` : ''}`}
        onClick={() => {
          if (onItemClick) onItemClick(value);
        }}
      >
        <div className={clsx(classes.weekEventTopRow)}>
          <Icon className={clsx(classes.weekEventTypeIcon)} style={{ color: itemColour }}>
            {typeInfo.icon || 'event'}
          </Icon>
          <span className={clsx(classes.weekEventTitle)} style={{ color: itemColour }}>
            {value?.title}
          </span>
        </div>
        <div className={clsx(classes.weekEventBottomRow)}>
          <span className={clsx(classes.weekEventTypeLabel)}>{typeInfo.label}</span>
          {value?.isDone && <Icon className={clsx(classes.weekEventDoneIcon)}>check_circle</Icon>}
        </div>
      </button>
    );
  };

  if (!weekDays.length) return null;

  const weekStart = weekDays[0].date;
  const weekEnd = weekDays[weekDays.length - 1].date;

  return (
    <div className={clsx(classes.weekView)}>
      <Typography variant="caption" className={clsx(classes.weekRangeLabel, 'cursor-default')}>
        {`Week: ${format(weekStart, 'dd MMM')} - ${format(weekEnd, 'dd MMM')}`}
      </Typography>
      <div className={clsx(classes.weekColumns)}>
        {weekDays.map(({ date, tasks }) => {
          const today = isToday(date);
          const hasOverflow = tasks.length > WEEK_DAY_VISIBLE_LIMIT;
          const visibleLimit = hasOverflow ? WEEK_DAY_VISIBLE_LIMIT - 1 : WEEK_DAY_VISIBLE_LIMIT;
          const visibleItems = tasks.slice(0, visibleLimit);
          const hiddenCount = Math.max(tasks.length - visibleLimit, 0);

          return (
            <div key={date.toISOString()} className={clsx(classes.dayColumn)}>
              <div className={clsx(classes.dayColumnHeader)}>
                <Typography className={clsx(classes.dayWeekday, today && classes.dayWeekdayToday, 'cursor-default')}>
                  {format(date, 'EEE')}
                </Typography>
                <Box className={clsx(classes.dayDateNumber, today && classes.dayDateNumberToday)}>
                  {format(date, 'd')}
                </Box>
              </div>
              <div className={clsx(classes.dayColumnBody)}>
                <div className={clsx(classes.weekEventsList)}>
                  {visibleItems.map((task) => renderWeekEventCard(task))}
                  {hiddenCount > 0 && (
                    <button
                      type="button"
                      className={clsx(classes.weekMoreItem)}
                      onClick={() => {
                        if (onOpenDayDialog) onOpenDayDialog(date);
                      }}
                    >
                      {t('dashboard:SCHEDULE_MORE', { count: hiddenCount })}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeekView;
